import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';
import { ensureCurrentUser, ensureUser } from '../../../util/data';
import { getUserPlan } from '../../../util/getUserPlan';
import verifiedBadgeImage from '../../../assets/Verificada.png';
import FavoriteButton from '../../../components/FavoriteButton/FavoriteButton';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  disableThumbnailSwipe: true,
  useBrowserFullscreen: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

// Helper function to detect if a URL is a video
const isVideoUrl = url => {
  if (!url) return false;
  const videoExtensions = /\.(mp4|mov|avi|mkv|webm)(\?.*)?$/i;
  return videoExtensions.test(url);
};

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videoRefs] = useState(new Map());
  const {
    intl,
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    videoLinks = [],
    user,
    listingId,
    viewImagesButtonMessageId,
    showThumbnails,
  } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const { plan, hasActivePlan } = getUserPlan(ensuredUser);

  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  // Combine images and videos into a single items array
  const items = useMemo(() => {
    const imageItems = images.map((img, i) => {
      return {
        // We will only use the image resource, but react-image-gallery
        // requires the `original` key from each item.
        original: '',
        alt: intl.formatMessage(
          { id: 'ListingImageGallery.imageAltText' },
          { index: i + 1, count: images.length + (videoLinks || []).length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ListingImageGallery.imageThumbnailAltText' },
          { index: i + 1, count: images.length + (videoLinks || []).length }
        ),
        thumbnail: img.attributes?.variants?.[thumbVariants[0]],
        image: img,
        type: 'image',
      };
    });

    const videoItems = (videoLinks || []).map((videoUrl, i) => {
      return {
        original: '',
        alt: intl.formatMessage(
          { id: 'ListingImageGallery.videoAltText' },
          { index: images.length + i + 1, count: images.length + (videoLinks || []).length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ListingImageGallery.videoThumbnailAltText' },
          { index: images.length + i + 1, count: images.length + (videoLinks || []).length }
        ),
        thumbnail: videoUrl, // Use video URL as thumbnail source
        videoUrl: videoUrl,
        type: 'video',
      };
    });

    return [...imageItems, ...videoItems];
  }, [images, videoLinks, intl, thumbVariants]);
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  // Handle slide change - pause all videos except the current one
  const handleSlideChange = useCallback(newIndex => {
    // Pause all videos
    videoRefs.forEach((videoElement, index) => {
      if (videoElement && index !== newIndex) {
        videoElement.pause();
      }
    });
    setCurrentIndex(newIndex);
  }, [videoRefs]);

  const renderItem = useCallback(item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          {hasActivePlan && plan === 'pro' && (
            <div className={css.verifiedBadgeContainer}>
              <img src={verifiedBadgeImage} alt={intl.formatMessage({ id: 'ListingCard.verified' })} className={css.verifiedBadge} />
            </div>
          )}
          {item.type === 'video' ? (
            <>
              {/* Background video with blur */}
              <video
                className={css.coverItem}
                src={item.videoUrl}
                alt={item.alt}
                muted
                loop
                ref={el => {
                  if (el) {
                    const itemIndex = items.indexOf(item);
                    videoRefs.set(itemIndex, el);
                  }
                }}
              />
              <div className={css.overlay}>
                {/* Foreground video with original aspect ratio */}
                <video
                  className={`${css.item} ${css.videoWithControls}`}
                  src={item.videoUrl}
                  alt={item.alt}
                  controls
                  controlsList="nodownload"
                  ref={el => {
                    if (el) {
                      const itemIndex = items.indexOf(item);
                      videoRefs.set(itemIndex, el);
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <ResponsiveImage
                rootClassName={css.coverItem}
                image={item.image}
                alt={item.alt}
                variants={imageVariants}
                {...imageSizesMaybe}
              />
              <div className={css.overlay}>
                <ResponsiveImage
                  rootClassName={css.item}
                  image={item.image}
                  alt={item.alt}
                  variants={imageVariants}
                  {...imageSizesMaybe}
                />
              </div>
            </>
          )}
        </div>
      </AspectRatioWrapper>
    );
  }, [aspectWidth, aspectHeight, isFullscreen, hasActivePlan, plan, items, videoRefs, imageVariants, imageSizesMaybe]);
  const renderThumbInner = useCallback(item => {
    return (
      <div>
        {item.type === 'video' ? (
          <div className={css.videoThumbContainer}>
            <video
              className={css.thumb}
              src={item.videoUrl}
              alt={item.thumbAlt}
              muted
              preload="metadata"
              style={{ width: '88px', height: '68px' }}
              onLoadedMetadata={e => {
                // Set current time to get first frame as thumbnail
                e.target.currentTime = 0.5;
              }}
              onError={e => {
                // Fallback: hide video and show play icon on dark background
                e.target.style.display = 'none';
              }}
            />
            <div className={css.playIcon}>▶</div>
          </div>
        ) : (
          <ResponsiveImage
            rootClassName={css.thumb}
            image={item.image}
            alt={item.thumbAlt}
            variants={thumbVariants}
            sizes="88px"
          />
        )}
      </div>
    );
  }, [thumbVariants]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        document.body.style.overflow = 'unset';
      }
    };
  }, []);

  const onScreenChange = useCallback(isFull => {
    setIsFullscreen(isFull);
    if (typeof window !== 'undefined') {
      if (isFull) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }
  }, []);

  const renderLeftNav = useCallback((onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={e => {
          // Prevent navigation when clicking near video controls
          const rect = e.currentTarget.getBoundingClientRect();
          const clickY = e.clientY - rect.top;
          const buttonHeight = rect.height;

          // If click is in bottom 20% (video controls area), don't navigate
          if (clickY > buttonHeight * 0.8) {
            e.stopPropagation();
            return;
          }

          onClick(e);
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  }, []);
  const renderRightNav = useCallback((onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={e => {
          // Prevent navigation when clicking near video controls
          const rect = e.currentTarget.getBoundingClientRect();
          const clickY = e.clientY - rect.top;
          const buttonHeight = rect.height;

          // If click is in bottom 20% (video controls area), don't navigate
          if (clickY > buttonHeight * 0.8) {
            e.stopPropagation();
            return;
          }

          onClick(e);
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  }, []);
  const renderFullscreenButton = useCallback((onClick, isFullscreenMode) => {
    // Hide fullscreen button if current item is a video
    const currentItem = items[currentIndex];
    if (currentItem && currentItem.type === 'video') {
      return null;
    }

    return isFullscreenMode ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage id={viewImagesButtonMessageId} values={{ count: items.length }} />
      </button>
    );
  }, [items, currentIndex, intl, viewImagesButtonMessageId]);

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      onSlide={handleSlideChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      renderCustomControls={() =>
        items[currentIndex]?.type === 'image' ? (
          <FavoriteButton listingId={listingId} className={css.favoriteButton} />
        ) : null
      }
      showThumbnails={showThumbnails}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
  videoLinks: [],
  viewImagesButtonMessageId: 'ListingImageGallery.viewImagesButton',
  showThumbnails: true,
};

const { string, arrayOf, bool } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),
  videoLinks: arrayOf(string),
  listingId: string,
  viewImagesButtonMessageId: string,
  showThumbnails: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
